<template>
  <div class="opinion-panel">
    <!-- 头部（两态不同） -->
    <div v-if="status === 'reviewing'" class="panel-header">审查意见生成中</div>
    <template v-else>
      <div class="panel-header completed-header">
        <span class="header-title">{{
          readonly ? `审查意见(${opinionList.length})` : "审查意见"
        }}</span>
        <span v-if="!readonly" class="header-count"
          >{{ appliedCount }}/{{ opinionList.length }}</span
        >
        <!-- PDF 只读态：右侧提示不支持定位与修订 -->
        <a-alert
          v-if="readonly"
          class="header-warning"
          message="PDF 的审查结果暂不支持定位与修订"
          type="warning"
          show-icon
        />
        <button
          v-if="!readonly"
          class="btn-apply-all"
          :disabled="applyingAll || cancelingAll"
          @click="onBtnClick"
        >
          {{ buttonText }}
        </button>
      </div>
      <!-- 筛选 tabs -->
      <div class="filter-tabs">
        <span
          v-for="tab in filterTabs"
          :key="tab.key"
          class="filter-tab"
          :class="{ active: activeFilter === tab.key }"
          @click="activeFilter = tab.key"
          >{{ tab.label }}</span
        >
      </div>
    </template>

    <!-- 可滚动内容区 -->
    <div ref="opinionScrollRef" class="opinion-scroll">
      <!-- ============ 审查中：骨架屏 ============ -->
      <template v-if="status === 'reviewing'">
        <div class="progress-card">
          <div class="progress-text">
            正在识别实质风险、形式问题和主体风险，请稍候…
          </div>
        </div>
        <div v-for="n in 2" :key="n" class="skeleton-box">
          <div class="skeleton-bar w170"></div>
          <div class="skeleton-bar w312"></div>
          <div class="skeleton-bar w263"></div>
          <div class="skeleton-bar w232"></div>
        </div>
      </template>

      <!-- ============ 审查完成：意见卡片列表 ============ -->
      <template v-else>
        <transition :name="transitionName">
          <div :key="activeFilter" class="opinion-list-wrap">
            <div class="opinion-list">
              <OpinionCard
                v-for="(op, idx) in filteredList"
                :key="op.num"
                :opinion="op"
                :display-num="activeFilter === 'all' ? null : idx + 1"
                :selected="selectedNum === op.num"
                :applied="!!op.applied"
                :readonly="readonly"
                @select="onSelect(op.num)"
                @locate="onLocate"
                @apply="onApply"
                @cancel="onCancel"
              />
            </div>
            <!-- 空态 -->
            <div v-if="filteredList.length === 0" class="empty-hint">
              暂无审查意见
            </div>
          </div>
        </transition>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import OpinionCard from "./OpinionCard.vue";

const props = defineProps({
  status: { type: String, default: "reviewing" },
  // 来自父组件（已 JSON.parse + 字段映射）的意见列表
  opinionList: { type: Array, default: () => [] },
  // 全部应用中（按钮置灰 + 显示"应用中…"）
  applyingAll: { type: Boolean, default: false },
  // 全部取消中（按钮置灰 + 显示"取消中…"）
  cancelingAll: { type: Boolean, default: false },
  // 只读模式（PDF 合同）：隐藏应用按钮/计数，卡片只读展示
  readonly: { type: Boolean, default: false },
});

const emit = defineEmits([
  "locate",
  "apply",
  "cancel",
  "apply-all",
  "cancel-all",
]);

/* 按钮两态：
 *   有已应用项 → "取消全部应用"（点击走取消逻辑）
 *   无已应用项 → "应用全部修改"（点击走应用逻辑）
 */
const buttonText = computed(() => {
  if (appliedCount.value > 0) {
    return props.cancelingAll ? "取消中…" : "取消应用";
  }
  return props.applyingAll ? "应用中…" : "应用修改";
});
const onBtnClick = () => {
  if (appliedCount.value > 0) {
    emit("cancel-all");
  } else {
    emit("apply-all");
  }
};

/* 筛选：key 对应后端 clauses[].reviewType 枚举；all 表示全部 */
const filterTabs = [
  { key: "all", label: "全部" },
  { key: "Substantive", label: "实质审查" },
  { key: "Formal", label: "形式审查" },
  { key: "Subject", label: "主体审查" },
];
const activeFilter = ref("all");
/* 切换方向：点击当前 tab 右侧的 tab → slide-next（内容从右往左进）；
 * 左侧 → slide-prev（从左往右进） */
const transitionName = ref("slide-next");

const filteredList = computed(() => {
  if (activeFilter.value === "all") return props.opinionList;
  return props.opinionList.filter((op) => op.reviewType === activeFilter.value);
});

/* 已应用计数（暂基于 opinion.applied，后端暂未给则维持 0） */
const appliedCount = computed(
  () => props.opinionList.filter((op) => op.applied).length,
);

/* 卡片选中状态：默认选中第一条 */
const selectedNum = ref(null);
watch(
  () => props.opinionList,
  (list) => {
    if (Array.isArray(list) && list.length > 0 && selectedNum.value == null) {
      selectedNum.value = list[0].num;
    }
  },
  { immediate: true },
);

/* 可滚动内容区 */
const opinionScrollRef = ref(null);

/* tab 切换：按新旧 tab 在 tabs 中的位置决定滑入方向；默认选中当前 tab
 * 下的第一条（覆盖之前的选中），内容区回到顶部 */
watch(activeFilter, (newKey, oldKey) => {
  const newIdx = filterTabs.findIndex((t) => t.key === newKey);
  const oldIdx = filterTabs.findIndex((t) => t.key === oldKey);
  transitionName.value = newIdx > oldIdx ? "slide-next" : "slide-prev";
  const list = filteredList.value;
  selectedNum.value = list.length > 0 ? list[0].num : null;
  if (opinionScrollRef.value) {
    opinionScrollRef.value.scrollTop = 0;
  }
});
const onSelect = (num) => {
  selectedNum.value = num;
};

const onLocate = (opinion) => {
  emit("locate", opinion);
};
// apply 载荷是 { opinion, version, content }，原样透传
const onApply = (payload) => {
  emit("apply", payload);
};
const onCancel = (opinion) => {
  emit("cancel", opinion);
};
</script>

<style lang="less" scoped>
.opinion-panel {
  width: 386px;
  min-width: 280px;
  flex-shrink: 0;
  background: white;
  border-left: 1px solid #e3eaf4;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  // border-radius: 0px 12px 0px 0px;
  padding-bottom: 16px;
}

/* 可滚动内容区（header / filter-tabs 固定，仅此处滚动）；
 * overflow-x: hidden 裁掉滑动过渡时超出边缘的内容，避免闪出横向滚动条 */
.opinion-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  padding-bottom: 16px;
}

/* 头部 */
.panel-header {
  padding: 16px;
  border-bottom: 1px solid #edf1f7;
  font-size: 16px;
  font-weight: 500;
  color: #18243b;
  flex-shrink: 0;
}
.completed-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
}
.header-title {
  font-size: 16px;
  font-weight: 500;
  color: #18243b;
}
.header-count {
  background: #eef3fb;
  border-radius: 30px;
  padding: 4px 12px;
  font-size: 12px;
  color: #52617a;
}
/* PDF 只读态右侧 warning（应用按钮隐藏后由它顶到最右） */
.header-warning {
  margin-left: auto;
  font-size: 12px;
  color: #d48806;
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 6px;
  padding: 3px 8px;
  line-height: 1.5;
  white-space: nowrap;
}
.btn-apply-all {
  margin-left: auto;
  background: #f2f7ff;
  border: 1px solid #cfe0ff;
  border-radius: 10px;
  height: 34px;
  width: 112px;
  font-size: 13px;
  color: #1768ff;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    background: #e6efff;
  }
}

/* 筛选 tabs */
.filter-tabs {
  display: flex;
  gap: 6px;
  padding: 10px 12px;
  border-bottom: 1px solid #edf1f7;
  flex-shrink: 0;
  justify-content: space-between;
}
.filter-tab {
  background: #f3f6fa;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  color: #64748f;
  cursor: pointer;
  border: 1px solid #f3f6fa;
  transition: all 0.15s;
  &.active {
    background: white;
    border: 1px solid #dce7fb;
    color: #1768ff;
  }
}

/* 进度卡 */
.progress-card {
  margin: 12px 16px 0;
  border: 1px solid #cfe0ff;
  background: #f7fbff;
  border-radius: 12px;
  padding: 16px 18px;
}
.progress-text {
  font-size: 14px;
  color: #1f2d45;
  line-height: 1.6;
}

/* 骨架屏 */
.skeleton-box {
  border: 1px solid #e5eaf2;
  border-radius: 12px;
  margin: 12px 16px 0;
  padding: 18px;
}
.skeleton-bar {
  background: #edf1f6;
  height: 12px;
  border-radius: 8px;
  margin: 6px 0;
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}
.w170 {
  width: 170px;
}
.w312 {
  width: 312px;
}
.w263 {
  width: 263px;
}
.w232 {
  width: 232px;
}

/* 意见列表 */
.opinion-list {
  padding-bottom: 0;
}

/* tab 切换过渡：新旧列表同时反向滑动（传送带式），不做淡入淡出 */
.slide-next-enter-active,
.slide-next-leave-active,
.slide-prev-enter-active,
.slide-prev-leave-active {
  transition: transform 0.35s cubic-bezier(0.25, 0.8, 0.35, 1);
}
/* 离场列表脱离文档流（与新列表重叠），滑出边缘后被容器裁掉 */
.slide-next-leave-active,
.slide-prev-leave-active {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
}
/* 点击右侧 tab：新内容从右往左进，旧内容继续往左滑出 */
.slide-next-enter-from {
  transform: translateX(100%);
}
.slide-next-leave-to {
  transform: translateX(-100%);
}
/* 点击左侧 tab：新内容从左往右进，旧内容继续往右滑出 */
.slide-prev-enter-from {
  transform: translateX(-100%);
}
.slide-prev-leave-to {
  transform: translateX(100%);
}

/* 空态 */
.empty-hint {
  padding: 40px 16px;
  text-align: center;
  color: #8b97aa;
  font-size: 13px;
}

@keyframes skeleton-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
</style>
